import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useGame } from '../context/GameContext';
import { usePortfolioStats } from '../hooks/usePortfolioStats';
import groupService from '../services/groupService';
import humanChatService from '../services/humanChatService';
import GroupRankingCardModern from '../components/GroupRankingCardModern';
import GroupRankingCardSkeleton from '../components/GroupRankingCardSkeleton';
import GroupPreviewCard from '../components/GroupPreviewCard';
import GroupsHeroSection from '../components/GroupsHeroSection';
import GroupsQuickActions from '../components/GroupsQuickActions';
import GroupDiscovery from '../components/GroupDiscovery';
import CreateGroupModal from '../components/CreateGroupModal';
import JoinGroupModal from '../components/JoinGroupModal';
import GroupChatTrigger from '../components/groups/GroupChatTrigger';
import GroupChatDrawer from '../components/groups/GroupChatDrawer';
import MobileTopBarClean from '../components/MobileTopBarClean';
import mainLogo from '../assets/images/mainlogo.png';
import '../styles/group-rankings.css';
import '../styles/group-rankings-modern.css';
import '../styles/group-chat-brutal.css';
import '../styles/global-white-backgrounds.css';
import { useIsMobile } from '../hooks/useIsMobile';

const GroupRankingsPage = () => {
  const { groupId, groupCode } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { loadPortfolioData, gameState } = useGame();
  const { getHeaderStats } = usePortfolioStats();

  // Memoize header stats to prevent excessive recalculations
  const headerStats = useMemo(() => {
    const stats = getHeaderStats();
    return {
      ...stats,
      netWorth: stats.netWorth || 0
    };
  }, [gameState, getHeaderStats]);

  // State management
  const [rankings, setRankings] = useState([]);
  const [groupInfo, setGroupInfo] = useState(null);
  const [userGroups, setUserGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [joinModalOpen, setJoinModalOpen] = useState(false);
  const [discoveryOpen, setDiscoveryOpen] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);

  // Mobile states
  const isMobile = useIsMobile();
  const [pullDistance, setPullDistance] = useState(0);
  const [pullThreshold] = useState(80);
  const touchStartRef = useRef(null);

  // Chat states
  const [chatDrawerOpen, setChatDrawerOpen] = useState(false);
  const [totalUnread, setTotalUnread] = useState(0);

  // Stable user removed handler
  const handleUserRemoved = useCallback((userId) => {
    setRankings(prev => prev.filter(r => r.userId !== userId));
    setGroupInfo(prev => prev ? { ...prev, memberCount: prev.memberCount - 1 } : prev);
  }, []);

  // Intersection Observer for card animations
  const cardObserverRef = useRef(null);


  // Load portfolio data for header stats
  useEffect(() => {
    if (user) {
      loadPortfolioData();
    }
  }, [user, loadPortfolioData]);

  // Load group rankings
  const loadGroupRankings = async (showRefreshLoader = false) => {
    if (!groupId) return;
    
    try {
      if (showRefreshLoader) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      console.log('📊 Loading group rankings for:', groupId);
      const response = await groupService.getGroupRankings(groupId);
      
      if (response.success) {
        setRankings(response.data.rankings || []);
        setGroupInfo({
          id: response.data.groupId,
          name: response.data.groupName,
          memberCount: response.data.memberCount,
          groupCode: response.data.groupCode || response.data.uniqueNumber,
          creatorId: response.data.creatorId || response.data.creator?._id
        });
        console.log('✅ Group rankings loaded:', response.data);
      } else {
        throw new Error(response.error || 'Failed to load group rankings');
      }
    } catch (error) {
      console.error('❌ Error loading group rankings:', error);
      setError(error.message);
      
      // If group not found or access denied, redirect to groups list
      if (error.message.includes('not found') || error.message.includes('access')) {
        setTimeout(() => navigate('/groups'), 2000);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Load user's groups with ranking info
  const loadUserGroups = async () => {
    if (!user) return;

    try {
      setGroupsLoading(true);
      console.log('📝 Loading user groups');
      const response = await groupService.getUserGroups();

      if (response.success) {
        // Filter out system/default groups that shouldn't be visible to users
        const filteredGroups = (response.data || []).filter(group => {
          const groupName = group.name?.toUpperCase() || '';
          const groupDesc = group.description?.toUpperCase() || '';

          // Filter out groups with system indicators
          const isSystemGroup =
            groupName === 'ALL' ||
            groupName.includes('DEFAULT') ||
            groupName.includes('SYSTEM') ||
            groupDesc.includes('DEFAULT GROUP FOR ALL USERS') ||
            groupDesc.includes('SYSTEM GROUP');

          return !isSystemGroup;
        });

        // Fetch rankings for each group to get user's position
        const groupsWithRankings = await Promise.all(
          filteredGroups.map(async (group) => {
            try {
              const rankingsResponse = await groupService.getGroupRankings(group._id || group.id);
              if (rankingsResponse.success && rankingsResponse.data.rankings) {
                const rankings = rankingsResponse.data.rankings;
                const userRanking = rankings.find(r => r.userId === user._id);
                const leaderRanking = rankings[0];

                return {
                  ...group,
                  memberCount: rankingsResponse.data.memberCount,
                  userRanking: userRanking ? {
                    rank: userRanking.rank,
                    profitLossPercent: userRanking.profitLossPercent,
                    netWorth: userRanking.netWorth,
                    movement: userRanking.movement || 0,
                    gapToLeader: leaderRanking && userRanking.rank > 1
                      ? (leaderRanking.profitLossPercent || 0) - (userRanking.profitLossPercent || 0)
                      : 0,
                    leaderName: leaderRanking?.nickname || leaderRanking?.firstName || leaderRanking?.username,
                    leaderPerformance: leaderRanking?.profitLossPercent
                  } : null
                };
              }
              return group;
            } catch (err) {
              console.log(`Failed to fetch rankings for group ${group.name}:`, err);
              return group;
            }
          })
        );

        setUserGroups(groupsWithRankings);
        console.log('✅ User groups loaded with rankings:', groupsWithRankings);
      } else {
        console.log('⚠️ No groups found or failed to load:', response.error);
        setUserGroups([]);
      }
    } catch (error) {
      console.error('❌ Error loading user groups:', error);
      setUserGroups([]);
    } finally {
      setGroupsLoading(false);
    }
  };

  // Track which groupId was loaded to prevent re-fetching on user reference changes
  const loadedGroupIdRef = useRef(null);

  // Handle different route cases
  useEffect(() => {
    if (groupCode) {
      // Check if it's a valid group code (4-8 digits) or MongoDB ID (24 hex chars)
      if (groupCode.length > 8) {
        // This is likely a MongoDB ID, not a valid group code
        console.error('Invalid group code in URL - appears to be a database ID');
        alert('Invalid group link. Please ask for a valid group code (4-8 digits) from the group creator.');
        navigate('/groups');
      } else {
        // Direct join group link - auto-open join modal
        setJoinModalOpen(true);
        setLoading(false);
      }
    } else if (groupId) {
      // Only load if we haven't loaded this group yet
      if (loadedGroupIdRef.current !== groupId) {
        loadedGroupIdRef.current = groupId;
        loadGroupRankings();
      }
    } else {
      // Groups list page - load user's groups
      loadedGroupIdRef.current = null;
      setLoading(false);
      loadUserGroups();
    }
  }, [groupId, groupCode, user]);

  // Track if we already auto-opened chat for this group
  const chatAutoOpenedRef = useRef(null);

  // Check for unread messages on initial load only
  useEffect(() => {
    if (!groupId || !groupInfo) return;
    // Only auto-open once per group
    if (chatAutoOpenedRef.current === groupId) return;

    const checkUnread = async () => {
      try {
        const unread = await humanChatService.getTotalUnreadCount(groupId);
        setTotalUnread(unread);

        // Auto-open chat drawer only on first load if there are unread messages
        if (unread > 0 && chatAutoOpenedRef.current !== groupId) {
          chatAutoOpenedRef.current = groupId;
          setChatDrawerOpen(true);
        }
      } catch (error) {
        console.error('Failed to check unread messages:', error);
      }
    };

    checkUnread();
  }, [groupId, groupInfo]);

  // Setup Intersection Observer for card entrance animations
  useEffect(() => {
    if (!cardObserverRef.current) {
      cardObserverRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry, index) => {
            if (entry.isIntersecting) {
              // Add staggered delay based on index
              setTimeout(() => {
                entry.target.classList.add('card-visible');
              }, index * 50);
              cardObserverRef.current.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
      );
    }

    // Observe all ranking cards
    const cards = document.querySelectorAll('.ranking-card-modern');
    cards.forEach((card) => {
      if (cardObserverRef.current) {
        cardObserverRef.current.observe(card);
      }
    });

    return () => {
      if (cardObserverRef.current) {
        cardObserverRef.current.disconnect();
      }
    };
  }, [rankings]); // Re-run when rankings change

  // Handle chat unread change
  const handleUnreadChange = useCallback((count) => {
    setTotalUnread(count);
  }, []);

  // Pull to refresh handlers - use ref for touchStart to avoid re-renders on tap
  const handleTouchStart = (e) => {
    if (!isMobile || window.scrollY > 0) return;

    const touch = e.touches[0];
    touchStartRef.current = { y: touch.clientY };
  };

  const handleTouchMove = (e) => {
    if (!isMobile || window.scrollY > 0) return;

    const touch = e.touches[0];
    const distance = touch.clientY - (touchStartRef.current?.y || 0);

    if (distance > 0) {
      setPullDistance(Math.min(distance, pullThreshold * 1.5));

      // Add visual feedback
      if (distance > pullThreshold) {
        document.body.style.backgroundColor = '#f0f8ff';
      }
    }
  };

  const handleTouchEnd = () => {
    if (!isMobile) return;

    if (pullDistance > pullThreshold) {
      loadGroupRankings(true);

      // Haptic feedback on refresh
      if ('vibrate' in navigator) {
        navigator.vibrate(20);
      }
    }

    if (pullDistance > 0) {
      setPullDistance(0);
    }
    touchStartRef.current = null;
    document.body.style.backgroundColor = '';
  };

  // Handle group actions
  const handleCreateGroup = () => {
    setCreateModalOpen(true);
  };

  const handleJoinGroup = () => {
    setJoinModalOpen(true);
  };

  const handleGroupCreated = (newGroup) => {
    console.log('✅ Group created successfully:', newGroup);
    setCreateModalOpen(false);
    
    // Navigate to the new group
    if (newGroup.group?._id) {
      navigate(`/groups/${newGroup.group._id}`);
    }
  };

  const handleGroupJoined = (joinedGroup) => {
    console.log('✅ Joined group successfully:', joinedGroup);
    setJoinModalOpen(false);
    
    // Navigate to the joined group
    if (joinedGroup._id) {
      navigate(`/groups/${joinedGroup._id}`);
    }
  };

  const handleShareGroup = () => {
    if (!groupInfo) return;
    
    // Only use the short group code, never the MongoDB ID
    if (!groupInfo.groupCode) {
      alert('This group does not have a shareable code yet. Please contact the group creator.');
      return;
    }
    
    const shareData = groupService.shareGroup(groupInfo.groupCode, groupInfo.name);
    
    if (navigator.share && isMobile) {
      // Use native sharing on mobile
      navigator.share({
        title: `Join "${groupInfo.name}" Investment Group`,
        text: shareData.text,
        url: shareData.link
      }).catch(console.error);
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(shareData.text)
        .then(() => alert('Group invite copied to clipboard!'))
        .catch(() => alert(`Group Code: ${shareData.code}\nShare this code with friends!`));
    }
  };

  const handleCopyGroupCode = async (groupCode, event) => {
    // Prevent card click navigation when copying
    if (event) {
      event.stopPropagation();
    }

    try {
      await navigator.clipboard.writeText(groupCode);
      setCopiedCode(true);

      // Haptic feedback on copy
      if ('vibrate' in navigator) {
        navigator.vibrate(15);
      }

      setTimeout(() => setCopiedCode(false), 2000);
    } catch (error) {
      // Fallback for older browsers
      alert(`Group Code: ${groupCode}\nCopy this code to share with friends!`);
    }
  };

  // Render loading state with skeleton
  if (loading && groupId) {
    return (
      <div className="group-rankings-page modern" style={{ background: '#F9FAFB', minHeight: '100vh', overflow: 'visible' }}>
        {/* TOP BAR - GAME STATISTICS */}
        <MobileTopBarClean stats={headerStats} />
        <div className="group-detail-modern" style={{ marginTop: isMobile ? '10px' : '90px' }}>
          <div className="rankings-container-modern">
            {[1, 2, 3, 4, 5].map((i) => (
              <GroupRankingCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Render error state
  if (error && !groupInfo) {
    return (
      <div className="group-rankings-page" style={{ overflow: 'visible' }}>
        {/* TOP BAR - GAME STATISTICS */}
        <MobileTopBarClean stats={headerStats} />
        <div className="error-container" style={{ marginTop: isMobile ? '10px' : '90px' }}>
          <div className="error-icon">⚠️</div>
          <h3>Unable to load group</h3>
          <p>{error}</p>
          <div className="error-actions">
            <button onClick={() => navigate('/groups')} className="btn btn-primary">
              Back to Groups
            </button>
            <button onClick={() => loadGroupRankings()} className="btn btn-secondary">
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render groups landing page if no specific group
  if (!groupId && !groupCode) {
    return (
      <div className="group-rankings-page modern" style={{ background: '#F9FAFB', minHeight: '100vh', overflow: 'visible' }}>
        {/* TOP BAR - GAME STATISTICS */}
        <MobileTopBarClean stats={headerStats} />

        <div className="groups-landing-modern" style={{ marginTop: isMobile ? '10px' : '90px' }}>
          {/* Back Button */}
          <button
            onClick={() => navigate('/dashboard')}
            className="back-btn-modern"
            title="Back to Dashboard"
            style={{
              background: '#000000',
              color: '#FFFFFF',
              border: '2px solid #000000'
            }}
          >
            <span className="back-icon" style={{ color: '#FFFFFF' }}>←</span>
            <span className="back-label" style={{ color: '#FFFFFF' }}>Back</span>
          </button>

          {/* Hero Section */}
          <GroupsHeroSection
            user={user}
            userGroups={userGroups}
          />

          {/* Quick Actions */}
          <GroupsQuickActions
            onBrowseGroups={() => setDiscoveryOpen(!discoveryOpen)}
            onJoinGroup={handleJoinGroup}
            onCreateGroup={handleCreateGroup}
            discoveryOpen={discoveryOpen}
          />

          {/* My Groups Section */}
          {userGroups.length > 0 && (
            <div className="my-groups-section-modern">
              <div className="section-header">
                <h2>My Groups</h2>
                <span className="group-count">{userGroups.length} group{userGroups.length !== 1 ? 's' : ''}</span>
              </div>
              {groupsLoading ? (
                <div className="loading-container-modern">
                  <div className="loading-spinner-modern" />
                  <p>Loading your groups...</p>
                </div>
              ) : (
                <div className="groups-list-modern">
                  {userGroups.map((group) => (
                    <GroupPreviewCard
                      key={group._id || group.id}
                      group={group}
                      userRanking={group.userRanking}
                      onCopyCode={handleCopyGroupCode}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Empty State */}
          {!groupsLoading && userGroups.length === 0 && (
            <div className="empty-groups-modern">
              <div className="empty-icon">🏆</div>
              <h3>No groups yet</h3>
              <p>Join or create a group to start competing with friends and family!</p>
            </div>
          )}

          {/* Group Discovery Section */}
          {discoveryOpen && (
            <div className="discovery-section-modern">
              <GroupDiscovery
                onGroupJoined={(group) => {
                  console.log('✅ Group joined from discovery:', group);
                  setDiscoveryOpen(false);
                  // Refresh user groups
                  loadUserGroups();
                }}
              />
            </div>
          )}
        </div>

        {/* Modals */}
        <CreateGroupModal
          isOpen={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onGroupCreated={handleGroupCreated}
        />

        <JoinGroupModal
          isOpen={joinModalOpen}
          onClose={() => setJoinModalOpen(false)}
          onGroupJoined={handleGroupJoined}
        />
      </div>
    );
  }

  // Main render for specific group
  return (
    <div
      className="group-rankings-page modern"
      style={{ background: '#F9FAFB', minHeight: '100vh', overflow: 'visible' }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* TOP BAR - GAME STATISTICS */}
      <MobileTopBarClean stats={headerStats} />

      {/* Pull to refresh indicator */}
      {pullDistance > 0 && (
        <div
          className="pull-refresh-indicator-modern"
          style={{
            transform: `translateY(${Math.min(pullDistance - 50, 30)}px)`,
            opacity: pullDistance / pullThreshold
          }}
        >
          {pullDistance > pullThreshold ? '🔄 Release to refresh' : '⬇️ Pull to refresh'}
        </div>
      )}

      <div className="group-detail-modern" style={{ marginTop: isMobile ? '10px' : '90px' }}>
        {/* Group Header */}
        {groupInfo && (
          <div className="group-header-modern">
            <div className="header-top-row">
              <button
                onClick={() => navigate('/groups')}
                className="back-btn-modern"
                title="Back to Groups"
                style={{
                  background: '#000000',
                  color: '#FFFFFF',
                  border: '2px solid #000000'
                }}
              >
                <span className="back-icon" style={{ color: '#FFFFFF' }}>←</span>
                <span className="back-label" style={{ color: '#FFFFFF' }}>Groups</span>
              </button>

              <div className="header-actions">
                <button
                  onClick={() => setChatDrawerOpen(true)}
                  className="action-btn-modern chat-btn"
                  title="Group Chat"
                >
                  💬
                  {totalUnread > 0 && (
                    <span className="unread-badge">{totalUnread > 9 ? '9+' : totalUnread}</span>
                  )}
                </button>
                <button
                  onClick={handleShareGroup}
                  className="action-btn-modern share-btn"
                  title="Share group invite"
                  disabled={!groupInfo?.groupCode}
                >
                  📤
                </button>
              </div>
            </div>

            <div className="group-title-section">
              <h1 className="group-name-modern" style={{ color: '#111827' }}>{groupInfo.name}</h1>
              <div className="group-meta-modern">
                <span
                  className="member-count"
                  style={{
                    color: '#6B7280',
                    background: '#F3F4F6',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: '1px solid #E5E7EB',
                    fontSize: '0.9rem',
                    fontWeight: '500'
                  }}
                >
                  👥 {groupInfo.memberCount} member{groupInfo.memberCount !== 1 ? 's' : ''}
                </span>
              </div>
            </div>

            {/* Prominent Group Code Section */}
            {groupInfo.groupCode && (
              <div className="group-code-section">
                <div className="group-code-info">
                  <span className="group-code-label">Invite Code</span>
                  <span className="group-code-value">{groupInfo.groupCode}</span>
                </div>
                <button
                  onClick={() => handleCopyGroupCode(groupInfo.groupCode)}
                  className={`copy-code-btn ${copiedCode ? 'copied' : ''}`}
                >
                  <span className="copy-icon">{copiedCode ? '✓' : '📋'}</span>
                  <span>{copiedCode ? 'Copied!' : 'Copy Code'}</span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Rankings List */}
        <div className="rankings-container-modern">
          {refreshing && (
            <div className="refresh-loader-modern">
              <div className="loading-spinner-modern" />
              <span>Updating rankings...</span>
            </div>
          )}

          {rankings.length > 0 ? (
            <div className="rankings-list-modern">
              {rankings.map((member, index) => (
                <GroupRankingCardModern
                  key={member.userId || index}
                  ranking={member}
                  currentUserId={user?._id}
                  isMobile={isMobile}
                  isGroupCreator={groupInfo?.creatorId === user?._id}
                  groupId={groupId}
                  onUserRemoved={handleUserRemoved}
                />
              ))}
            </div>
          ) : (
            <div className="empty-rankings-modern">
              <div className="empty-icon">👥</div>
              <h3>No rankings yet</h3>
              <p>Invite friends to join the group and start competing!</p>
              <button onClick={handleShareGroup} className="btn-modern primary">
                Share Invite
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <CreateGroupModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onGroupCreated={handleGroupCreated}
      />

      <JoinGroupModal
        isOpen={joinModalOpen}
        onClose={() => setJoinModalOpen(false)}
        onGroupJoined={handleGroupJoined}
        prefillCode={groupCode}
      />

      {/* Chat Drawer */}
      <GroupChatDrawer
        isOpen={chatDrawerOpen}
        onClose={() => setChatDrawerOpen(false)}
        groupId={groupId}
        onUnreadChange={handleUnreadChange}
      />
    </div>
  );
};

export default GroupRankingsPage;